import { createElement, ReactNode } from "react";
import { ColumnOptions, ColumnPin, View } from "@common/types/views/views";
import { flexRender, type RowData } from "@tanstack/react-table";
import {
    isLeafHeaderContext,
    type ViewTableColumnDef,
    type ViewTableCellContext,
    type ViewTableHeaderContext,
} from "./table";
import { ColumnGroupConfig, ViewConfig } from "@lib/views";
import { SortableHeader } from "@design-system/SortableHeader/SortableHeader";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { ViewTableCheckboxCell } from "../ViewTableCheckboxCell";
import {
    ViewTableCheckboxHeader,
    ViewTableCheckboxHeaderProps,
    SelectAllMatchingControl,
} from "../ViewTableCheckboxHeader";
import { ExpandCollapseCell } from "../ExpandCollapseCell";
import { hasGroupBoundary } from "../ViewTable.lib";
import {
    FILLER_COLUMN_ID,
    LEADING_FILLER_COLUMN_ID,
    TRAILING_FILLER_MIN_WIDTH,
} from "./fillerColumns";
import { DEFAULT_SELECT_COLUMN_SIZE } from "./useSelectColumnSize";
import { shouldRenderSelectAll } from "./selectAll";

interface GroupLeaf<TData extends RowData> {
    def: ViewTableColumnDef<TData>;
    pin: ColumnPin | "center";
    id: string;
    groupPath: string[];
}

interface NestLeavesByGroupPathOptions<TData extends RowData> {
    leaves: Array<GroupLeaf<TData>>;
    groups: Record<string, ColumnGroupConfig> | undefined;
}

interface NestAtDepthOptions<
    TData extends RowData,
> extends NestLeavesByGroupPathOptions<TData> {
    depth: number;
}

function buildGroupColumnDef<TData extends RowData>({
    groupId,
    leaves,
    groups,
    depth,
}: NestAtDepthOptions<TData> & {
    groupId: string;
}): ViewTableColumnDef<TData> {
    const groupConfig = groups?.[groupId];
    if (!groupConfig) {
        throw new Error(
            `ViewConfig.groups["${groupId}"] is missing ` +
                `(referenced by columns: ${leaves.map((l) => l.id).join(", ")}).`,
        );
    }
    const pins = new Set(leaves.map((l) => l.pin));
    if (pins.size > 1) {
        throw new Error(
            `Column group "${groupId}" straddles a pin boundary ` +
                `(leaves: ${leaves.map((l) => l.id).join(", ")}). ` +
                `All columns in a group must share the same pin.`,
        );
    }
    const groupPin = [...pins][0];
    // Nested groups always separate at their right edge so each bracket level reads
    // distinctly; a top-level group inherits the boundary from its rightmost leaf.
    // Group headers read their own meta, so a nested flag never leaks to data rows.
    const isNestedGroup = depth >= 1;
    return {
        id: groupId,
        header: () => groupConfig.header as ReactNode,
        columns: nestAtDepth({ leaves, groups, depth: depth + 1 }),
        meta: {
            pin: groupPin === "center" ? undefined : groupPin,
            isGroup: true,
            isGroupBoundary:
                isNestedGroup ||
                hasGroupBoundary(leaves[leaves.length - 1]?.def.meta),
        },
    };
}

function nestAtDepth<TData extends RowData>({
    leaves,
    groups,
    depth,
}: NestAtDepthOptions<TData>): Array<ViewTableColumnDef<TData>> {
    const result: Array<ViewTableColumnDef<TData>> = [];
    let index = 0;
    while (index < leaves.length) {
        const groupId = leaves[index].groupPath[depth];
        if (groupId === undefined) {
            result.push(leaves[index].def);
            index += 1;
            continue;
        }
        const groupLeaves: Array<GroupLeaf<TData>> = [];
        while (
            index < leaves.length &&
            leaves[index].groupPath[depth] === groupId
        ) {
            groupLeaves.push(leaves[index]);
            index += 1;
        }
        result.push(
            buildGroupColumnDef({
                groupId,
                leaves: groupLeaves,
                groups,
                depth,
            }),
        );
    }
    return result;
}

export function nestLeavesByGroupPath<TData extends RowData>({
    leaves,
    groups,
}: NestLeavesByGroupPathOptions<TData>): Array<ViewTableColumnDef<TData>> {
    return nestAtDepth({ leaves, groups, depth: 0 });
}

function flattenLeafColumns<TData extends RowData>(
    columns: Array<ViewTableColumnDef<TData>>,
): Array<ViewTableColumnDef<TData>> {
    return columns.flatMap((column) =>
        "columns" in column && column.columns
            ? flattenLeafColumns([...column.columns])
            : [column],
    );
}

/** Stable header component for non-sortable columns with a label. Reads text from column meta. */
const BreakableHeaderCell = <TData extends { id: number | string }>({
    column,
}: ViewTableHeaderContext<TData, unknown>) => {
    const text = column.columnDef.meta?.headerLabel;
    if (!text) {
        return null;
    }
    return createElement(BreakableText, { text, showTooltip: true });
};
BreakableHeaderCell.displayName = "BreakableHeaderCell";

interface BuildColumnsOptions<
    TData extends { id: number | string },
    TColumn extends string,
    TSort extends string,
> {
    view: View;
    config: ViewConfig<TData, TColumn, TSort>;
    sort?: TSort;
    onSortChange?: (sort: TSort) => void;
    onSortRemove?: () => void;
    // Shows a spinner in place of the sort arrow on the active sort column
    // while true. Intended for consumers whose data hook exposes a
    // refetch/fetching state distinct from the initial-load state.
    isSortLoading?: boolean;
    // Opt-in: when provided, the selection-column header checkbox is driven by
    // this all-matching control instead of TanStack's loaded page rows.
    selectAllControl?: SelectAllMatchingControl;
    centerColumns?: boolean;
    // Width of the selection column; see `useSelectColumnSize`.
    selectColumnSize?: number;
}

function createFillerColumn<TData extends RowData>({
    id,
    minWidth,
}: {
    id: string;
    minWidth: string;
}): ViewTableColumnDef<TData> {
    return {
        id,
        header: "",
        accessorKey: "",
        meta: { fillerMinWidth: minWidth },
        minSize: 0,
        maxSize: undefined,
    };
}

interface BuildColumnsResult<TData extends RowData> {
    columns: Array<ViewTableColumnDef<TData>>;
    columnPinning: { start: string[]; end: string[] };
}

/**
 * Builds column definitions for the ViewTable.
 * Handles selection column, sorting headers, expand/collapse cells, filler columns, and pinning.
 */
export function buildColumns<
    TData extends { id: number | string },
    TColumn extends string,
    TSort extends string,
>({
    view,
    config,
    sort,
    onSortChange,
    onSortRemove,
    isSortLoading,
    selectAllControl,
    centerColumns,
    selectColumnSize = DEFAULT_SELECT_COLUMN_SIZE,
}: BuildColumnsOptions<TData, TColumn, TSort>): BuildColumnsResult<TData> {
    // Selection column
    const selectionColumn: ViewTableColumnDef<TData> | null =
        view.options?.bulkActions === true
            ? {
                  id: "select",
                  header: (headerCtx: ViewTableHeaderContext<TData>) =>
                      shouldRenderSelectAll(view.options) &&
                      isLeafHeaderContext(headerCtx)
                          ? createElement<ViewTableCheckboxHeaderProps<TData>>(
                                ViewTableCheckboxHeader,
                                {
                                    table: headerCtx.table,
                                    control: selectAllControl,
                                    isAllPageRowsSelected:
                                        headerCtx.isAllPageRowsSelected,
                                    isSomePageRowsSelected:
                                        headerCtx.isSomePageRowsSelected,
                                },
                            )
                          : null,
                  cell: ViewTableCheckboxCell,
                  minSize: selectColumnSize,
                  maxSize: selectColumnSize,
                  meta: { pin: "left" },
              }
            : null;

    // leafMetas shares each leaf's meta object by reference so the boundary pass
    // below can flag it post-hoc. leafHasExplicitGroupPath gates that pass to
    // N-level (`groupPath`) leaves, keeping separators off legacy `groupId` views.
    const orderedLeaves: Array<GroupLeaf<TData>> = [];
    const leafMetas: Array<Record<string, unknown>> = [];
    const leafHasExplicitGroupPath: boolean[] = [];

    for (const column of view.columns) {
        const columnConfig = config.columns[column.id as TColumn];

        if (!columnConfig) {
            continue;
        }

        const isPinned = column.options?.pin !== undefined;
        const isExpandColumn =
            config.nesting?.enabled &&
            config.nesting.expandColumn === column.id;

        // For non-sortable columns with a label, use the stable BreakableHeaderCell
        // component (reads label from column meta). Empty headers get a plain string.
        // For sortable columns use a named function so the React profiler shows
        // "SortableHeaderCell" instead of "Anonymous".
        let headerDef: ViewTableColumnDef<TData>["header"];
        if (columnConfig.headerCell) {
            // Column supplies a fully custom header renderer (e.g. select-all
            // checkbox). Use it verbatim; it receives the HeaderContext.
            headerDef = columnConfig.headerCell;
        } else if (columnConfig.sort) {
            const asc = columnConfig.sort.asc as TSort;
            const desc = columnConfig.sort.desc as TSort;
            const defaultOrder = columnConfig.sort.defaultOrder ?? "desc";
            const headerLabel = columnConfig.header;
            const SortableHeaderCell = () =>
                createElement(
                    SortableHeader as React.ComponentType<{
                        text: string;
                        sort: TSort | undefined;
                        asc: TSort;
                        desc: TSort;
                        onChange: ((sort: TSort) => void) | undefined;
                        onSortRemove: (() => void) | undefined;
                        defaultOrder: "asc" | "desc";
                        isLoading: boolean | undefined;
                    }>,
                    {
                        text: headerLabel,
                        sort,
                        asc,
                        desc,
                        onChange: onSortChange,
                        onSortRemove,
                        defaultOrder,
                        isLoading: isSortLoading,
                    },
                );
            SortableHeaderCell.displayName = "SortableHeaderCell";
            headerDef = SortableHeaderCell;
        } else {
            const headerLabel = String(columnConfig.header ?? "");
            headerDef = headerLabel ? BreakableHeaderCell : "";
        }

        const meta: Record<string, unknown> = {
            ...(column.options ?? {}),
        };
        // Store header label in meta so BreakableHeaderCell can read it
        // without needing a per-column wrapper function.
        if (columnConfig.header) {
            meta.headerLabel = String(columnConfig.header);
        }
        if (columnConfig.headerAction) {
            meta.headerAction = columnConfig.headerAction;
        }
        if (columnConfig.align) {
            meta.align = columnConfig.align;
        }
        if (columnConfig.headerTierDepth !== undefined) {
            meta.headerTierDepth = columnConfig.headerTierDepth;
        }

        const columnDef: ViewTableColumnDef<TData> = {
            id: column.id,
            header: headerDef,
            meta,
            minSize: column.options?.minSize ?? columnConfig.minSize,
            maxSize: column.options?.maxSize ?? columnConfig.maxSize,
            size:
                column.options?.size ??
                (isPinned ? columnConfig.minSize : undefined),
        };

        if (isExpandColumn) {
            // Always wrap the expand column with ExpandCollapseCell
            const originalCell = columnConfig.cell;
            const hideDisclosureOnLeafChildren =
                config.nesting?.hideDisclosureOnLeafChildren;
            const ExpandColumnCell = (
                context: ViewTableCellContext<TData, unknown>,
            ) =>
                createElement(ExpandCollapseCell<TData>, {
                    row: context.row,
                    hideDisclosureOnLeafChildren,
                    children: originalCell
                        ? flexRender(originalCell, context)
                        : String(context.getValue()),
                });
            ExpandColumnCell.displayName = "ExpandColumnCell";
            columnDef.cell = ExpandColumnCell;
        } else if (columnConfig.cell) {
            columnDef.cell =
                columnConfig.cell as ViewTableColumnDef<TData>["cell"];
        }

        const leafDef: ViewTableColumnDef<TData> = {
            ...columnDef,
            accessorKey: columnConfig.accessorKey,
        };
        const leafPin: ColumnPin | "center" = column.options?.pin ?? "center";
        const hasExplicitGroupPath = columnConfig.groupPath !== undefined;
        const effectivePath =
            columnConfig.groupPath ??
            (columnConfig.groupId ? [columnConfig.groupId] : []);

        orderedLeaves.push({
            def: leafDef,
            pin: leafPin,
            id: column.id,
            groupPath: effectivePath,
        });
        leafMetas.push(meta);
        leafHasExplicitGroupPath.push(hasExplicitGroupPath);
    }

    // Only N-level (`groupPath`) leaves qualify, so legacy `groupId` views (e.g.
    // trial balance) keep borderless headers. The trailing group has no neighbour.
    for (const [index, leaf] of orderedLeaves.slice(0, -1).entries()) {
        if (!leafHasExplicitGroupPath[index]) {
            continue;
        }
        const currentTopGroup = leaf.groupPath[0];
        const nextTopGroup = orderedLeaves[index + 1].groupPath[0];
        if (currentTopGroup !== undefined && currentTopGroup !== nextTopGroup) {
            leafMetas[index].isGroupBoundary = true;
        }
    }

    const baseColumns: Array<ViewTableColumnDef<TData>> = nestLeavesByGroupPath(
        {
            leaves: orderedLeaves,
            groups: config.groups,
        },
    );

    // Add selection column at the beginning
    if (selectionColumn) {
        baseColumns.unshift(selectionColumn);
    }

    // Flatten to leaves: group wrappers lack maxSize and would suppress the filler.
    const leafColumns = flattenLeafColumns(baseColumns);
    const autoMaxWidthColumns = leafColumns.filter(
        (column) =>
            column.maxSize === undefined &&
            (column.meta as ColumnOptions).pin === undefined,
    );

    // If there are no columns in the center with unconstrained width
    // Add a filler column to ensure the table stretches to fill available space
    if (autoMaxWidthColumns.length === 0) {
        // A column pinned right sorts after the filler and keeps the end inset itself.
        const isFillerLast = leafColumns.every(
            (column) => column.meta?.pin !== "right",
        );
        const fillerMinWidth = isFillerLast ? TRAILING_FILLER_MIN_WIDTH : "0px";
        baseColumns.push(
            createFillerColumn({
                id: FILLER_COLUMN_ID,
                minWidth: fillerMinWidth,
            }),
        );
        // A second filler before the centre columns splits the leftover width
        // evenly, so narrow content is centred rather than flush left. It takes
        // the trailing filler's minimum, since the grid shares leftover width
        // on top of each minimum.
        if (centerColumns) {
            baseColumns.unshift(
                createFillerColumn({
                    id: LEADING_FILLER_COLUMN_ID,
                    minWidth: fillerMinWidth,
                }),
            );
        }
    }

    // Sort columns by pinning order: left pinned, center (unpinned), right pinned
    const sortedColumns = baseColumns.toSorted((a, b) => {
        const aPinning = a.meta?.pin ?? "center";
        const bPinning = b.meta?.pin ?? "center";

        const pinOrder = { left: 0, center: 1, right: 2 };
        return (
            pinOrder[aPinning as keyof typeof pinOrder] -
            pinOrder[bPinning as keyof typeof pinOrder]
        );
    });

    // Build column pinning state
    const left: string[] = [];
    const right: string[] = [];
    if (selectionColumn) {
        left.push("select");
    }
    for (const column of view.columns) {
        if (column.options?.pin === "left") {
            left.push(column.id);
        } else if (column.options?.pin === "right") {
            right.push(column.id);
        }
    }

    return {
        columns: sortedColumns,
        columnPinning: { start: left, end: right },
    };
}
